// ──────────────────────────────────────────────────────────────────────────
//  Buying Station Order (Purchase Order) bulk-import — shared front-end helpers.
//  Adapts API records to the view-model the components consume.
//
//  v2 template columns:
//    Member ID  |  Purchase Date  |  Cherry Grade  |  Quantity (Kg)  |  Price/Kg  |  Receipt No
// ──────────────────────────────────────────────────────────────────────────

export const BULK_RESULT_STORAGE_KEY = "buyingStationOrderBulkImportResult";

export const SERVER_RECORD_STATUS = {
  SUCCESS: "SUCCESS",
  ERROR_DETECTED: "ERROR_DETECTED",
  DELETED: "DELETED",
};

export const SERVER_BULK_IMPORT_STATUS = {
  VALIDATING: "VALIDATING",
  SUCCESS: "SUCCESS",
  CANCELLED: "CANCELLED",
};

/** API error code → i18n key (friendly, param-free) for tooltips / field messages. */
export const SERVER_ERROR_CODE_I18N = {
  MISSING_MEMBER_ID:              "errorMissingMemberId",
  INVALID_MEMBER_ID_FORMAT:       "errorInvalidMemberIdFormat",
  INVALID_FARMER_NAME:            "errorInvalidFarmerName",
  INVALID_BUYING_STATION_NAME:    "errorInvalidBuyingStationName",
  INVALID_FARM_ID:                "errorInvalidFarm",
  INVALID_PURCHASE_DATE:          "errorInvalidPurchaseDate",
  MISSING_PURCHASE_DATE:          "errorMissingPurchaseDate",
  MISSING_COFFEE_CHERRY_QUANTITY: "errorMissingQuantity",
  INVALID_COFFEE_CHERRY_QUANTITY: "errorInvalidQuantity",
  MISSING_COFFEE_CHERRY_QUALITY:  "errorMissingQuality",
  INVALID_COFFEE_CHERRY_QUALITY:  "errorInvalidQuality",
  MISSING_PRICE:                  "errorMissingPrice",
  INVALID_PRICE:                  "errorInvalidPrice",
};

/** API error code → short i18n label (filter chips). */
export const SERVER_ERROR_CODE_LABEL_I18N = {
  MISSING_MEMBER_ID:              "chipMissingMemberId",
  INVALID_MEMBER_ID_FORMAT:       "chipInvalidMemberIdFormat",
  INVALID_FARMER_NAME:            "chipInvalidFarmerName",
  INVALID_BUYING_STATION_NAME:    "chipInvalidBuyingStationName",
  INVALID_FARM_ID:                "chipInvalidFarm",
  INVALID_PURCHASE_DATE:          "chipInvalidPurchaseDate",
  MISSING_PURCHASE_DATE:          "chipMissingPurchaseDate",
  MISSING_COFFEE_CHERRY_QUANTITY: "chipMissingQuantity",
  INVALID_COFFEE_CHERRY_QUANTITY: "chipInvalidQuantity",
  MISSING_COFFEE_CHERRY_QUALITY:  "chipMissingQuality",
  INVALID_COFFEE_CHERRY_QUALITY:  "chipInvalidQuality",
  MISSING_PRICE:                  "chipMissingPrice",
  INVALID_PRICE:                  "chipInvalidPrice",
};

export function isSuccessRecord(record) {
  return String(record?.status || "").toUpperCase() === SERVER_RECORD_STATUS.SUCCESS;
}

export function isErrorRecord(record) {
  return String(record?.status || "").toUpperCase() === SERVER_RECORD_STATUS.ERROR_DETECTED;
}

/** Map one API record to the view-model the table/dialogs consume. */
export function mapServerRecord(record) {
  const rec = record.recordedJsonData || {};
  return {
    id: record.id,
    status: record.status,
    isError: String(record.status).toUpperCase() === SERVER_RECORD_STATUS.ERROR_DETECTED,
    errorCodes: record.errorCodes || [],
    errorMetadata: record.errorMetadata || {},
    recordedJsonData: rec,
    payloadJsonData: record.payloadJsonData || null,
    buyingStationOrderId: record.buyingStationOrderId || null,
  };
}

/** Map the validate / get-by-id payload to the detail object used by the page. */
export function mapServerImport(data = {}) {
  const records = (data.records || []).map(mapServerRecord);
  const successRecordsCount = records.filter(isSuccessRecord).length;
  const issuesRecordsCount = records.filter(isErrorRecord).length;
  return {
    bulkImportId: data.bulkImportId != null ? data.bulkImportId : data.id,
    status: data.status || null,
    originalFileName: data.originalFileName || null,
    downloadUrl: data.downloadUrl || null,
    uploadedBy: data.uploadedBy || null,
    totalRecordsCount: data.totalRecordsCount != null ? data.totalRecordsCount : records.length,
    errorRecordsCount: data.errorRecordsCount != null ? data.errorRecordsCount : issuesRecordsCount,
    successRecordsCount,
    issuesRecordsCount,
    importedRecordsCount: successRecordsCount,
    records,
  };
}

/** Build the edit payload sent to the update-record endpoint (resolved IDs). */
export function buildRecordUpdatePayload(form) {
  return {
    purchaseDate:    form.purchaseDate || null,
    buyingStationId: form.buyingStationId || null,
    farmerId:        form.farmerId || null,
    farmId:          form.farmId || null,
    plantationId:    form.plantationId || null,
    speciesId:       form.speciesId || null,
    varietyId:       form.varietyId || null,
    coffeeTypeId:    form.coffeeTypeId || null,
    coffeeQualityId: form.coffeeQualityId || null,
    coffeeCherryQlty: form.coffeeCherryQlty || null,
    coffeeCherryQty:
      form.coffeeCherryQty === "" || form.coffeeCherryQty == null
        ? null
        : Number(form.coffeeCherryQty),
    perKgPrice:
      form.perKgPrice === "" || form.perKgPrice == null
        ? null
        : Number(form.perKgPrice),
    receiptNo: form.receiptNo || null,
  };
}
